<script setup lang="ts">
/**
 * 商品情報の編集モーダル。
 *
 * 左に入力欄、右にNGワードのマーキング付きプレビューを並べる。
 * 「編集中も下線・ホバー説明を見たい」という要望に対して、
 *   - 上下分割(要望により却下)
 *   - textareaに直接重ねる(フォント同期の精度リスクが高いため却下)
 *   - フォーカスで自動切替(編集中は見えない項目が出るため要望に合わない)
 * を検討した結果、左右分割+スクロール同期を採用した。
 *
 * 【折り返し位置を一致させるための対策】
 * left/right で全く同じCSS変数(フォント・サイズ・行間・幅・パディング)を
 * 共有する。textareaとdivはブラウザの改行計算が別物なので、
 * 100%のピクセル一致は理論上保証できない。実用上ズレを起こしにくくするため:
 *   - scrollbar-gutter:stable を両方に指定し、スクロールバーの有無による
 *     実効幅の差を無くす
 *   - box-sizing/padding/border-widthを完全に揃える
 *   - フォントは等幅ではなく可変幅だが、v-readableと同じ指定を流用する
 *
 * 【対象範囲】
 * 審査画面(OfferDetailPanel)に表示されている項目のみ編集可能にする。
 * 商品編集画面にしかない項目(EAN以外の識別子、重量、発売日等)は、
 * 値の変更が起きないよう保存時にそのまま渡す(非表示・編集不可)。
 * 消費税率(offer.taxId)は別APIで審査画面のインライン<select>から
 * 既に編集できるため、このモーダルの対象外。
 */
import { reactive, ref, computed, watch, nextTick } from 'vue'
import { useProductDetail, useSaveProductDetailMutation, useRefreshProductFromCatalogMutation, useCheckNgWords } from '@/api/queries'
import { useResultModalStore } from '@/stores/resultModal'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useAuthStore } from '@/stores/auth'
// NgMarkedTextは現在未使用(商品名/特徴/商品説明すべてNgEditableAreaに統一したため)。
// 下部にコメントアウトで残した旧実装(左右分割+プレビュー)を復活させる場合は、
// `import NgMarkedText from './NgMarkedText.vue'` を再度追加すること。
import NgEditableArea from './NgEditableAreaCM.vue'  // CodeMirror版に切り替え
import type { NgWordMatch } from '@/types/api'

const props = defineProps<{
  asin: string
  regionCode: string
}>()
const emit = defineEmits<{ close: []; saved: [] }>()

const { data: detail, isLoading } = useProductDetail(ref(props.asin), ref(props.regionCode))
const product = computed(() => detail.value?.product ?? null)
// カテゴリの選択肢。商品編集画面(ProductEditView)と同じく getProductDetail の
// レスポンスにまとめて含まれているので、追加のAPI呼び出しは不要。
const categories = computed(() => detail.value?.categories ?? [])

interface EditForm {
  title: string
  categoryCode: string
  brand: string
  model: string
  color: string
  size: string
  ean: string
  adultFlg: boolean
  features: string
  editorialReview: string
}
function blankForm(): EditForm {
  return { title: '', categoryCode: '', brand: '', model: '', color: '', size: '', ean: '', adultFlg: false, features: '', editorialReview: '' }
}
const form = reactive<EditForm>(blankForm())

watch(
  product,
  (p) => {
    if (!p) return
    form.title = p.Title ?? ''
    form.categoryCode = p.CategoryCode ?? ''
    form.brand = p.Brand ?? ''
    form.model = p.Model ?? ''
    form.color = p.Color ?? ''
    form.size = p.Size ?? ''
    form.ean = p.EAN ?? ''
    form.adultFlg = p.AdultFlg === '1'
    // 【重要】DBの保存値には \r\n(CRLF)が混ざっていることがある。
    // NgEditableAreaCM(CodeMirror)は表示時に \r\n を自動で \n(1文字)に
    // 詰めてしまうため、ここでform側も先に \n へ統一しておかないと、
    // 「NGワードチェックに送った文字列(CRLFのまま)」と「エディタが実際に
    // 表示している文字列(LF)」の改行の数え方が食い違い、行を追うごとに
    // マーキング位置が1文字ずつずれていく(改行の数だけズレが蓄積する)。
    form.features = (p.Features ?? '').replace(/\r\n/g, '\n')
    form.editorialReview = (p.EditorialReview ?? '').replace(/\r\n/g, '\n')
  },
  { immediate: true },
)

// ---- NGワードのリアルタイム再チェック ----
// 入力を続けている間に何度も投げないよう、確定してから少し待って呼ぶ。
const checkMutation = useCheckNgWords()
const liveNgWords = ref<NgWordMatch[]>([])
let debounceTimer: ReturnType<typeof setTimeout> | null = null

function scheduleCheck() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    checkMutation.mutate(
      {
        title: form.title || undefined,
        model: form.model || undefined,
        color: form.color || undefined,
        size: form.size || undefined,
        features: form.features || undefined,
        description: form.editorialReview || undefined,
      },
      { onSuccess: (res) => { liveNgWords.value = res.ngWords } },
    )
  }, 400)
}
watch(() => [form.title, form.brand, form.model, form.color, form.size, form.features, form.editorialReview], scheduleCheck, { immediate: true })

// ---- 特徴・商品説明: 左右スクロール同期 ----
// 「片方をスクロールしたらもう片方も追従する」を双方向でやると、
// scrollイベントが相互に発火してループする恐れがあるため、
// 「今どちらが操作の主(source)か」を記録して、反対側にだけ反映する。
function useSyncScroll() {
  const leftEl = ref<HTMLTextAreaElement | null>(null)
  const rightEl = ref<HTMLDivElement | null>(null)
  let source: 'left' | 'right' | null = null

  function onLeftScroll() {
    if (source === 'right') { source = null; return }
    source = 'left'
    if (rightEl.value && leftEl.value) rightEl.value.scrollTop = leftEl.value.scrollTop
  }
  function onRightScroll() {
    if (source === 'left') { source = null; return }
    source = 'right'
    if (leftEl.value && rightEl.value) leftEl.value.scrollTop = rightEl.value.scrollTop
  }
  return { leftEl, rightEl, onLeftScroll, onRightScroll }
}
const featuresSync = useSyncScroll()
const descriptionSync = useSyncScroll()

// テキストが変わって行数が減ったときなど、スクロール位置がずれたら
// 次の描画後に右側を左側へ合わせ直す
watch(() => form.features, () => nextTick(() => {
  if (featuresSync.leftEl.value && featuresSync.rightEl.value) {
    featuresSync.rightEl.value.scrollTop = featuresSync.leftEl.value.scrollTop
  }
}))
watch(() => form.editorialReview, () => nextTick(() => {
  if (descriptionSync.leftEl.value && descriptionSync.rightEl.value) {
    descriptionSync.rightEl.value.scrollTop = descriptionSync.leftEl.value.scrollTop
  }
}))

// ---- 保存 ----
const resultModal = useResultModalStore()
const confirmModal = useConfirmModalStore()
const saveMutation = useSaveProductDetailMutation()
const auth = useAuthStore()

// ---- Amazonカタログから更新 ----
// VB版 ProductOverwriterDetailWindow.xaml.vb ButtonRefresh_Click 相当。
// このモーダルはフォーム項目を審査画面表示分に絞っているが(冒頭コメント参照)、
// 発売日・販売ランキング・メーカー希望価格・attributes/keyword/identifiers/dimensions は
// フォームには出さず「取得はするが非表示」のまま保存時にそのまま渡す設計だったため、
// 取得結果をいったんここに退避し、保存時は取得済みの値があればそれを優先する
// (ProductEditView.vue の fetchedRawFields と同じ考え方)。
const fetchedRawFields = ref<{
  attributes: string | null
  keyword: string | null
  identifiers: string | null
  dimensions: string | null
  releaseDate: string | null
  salesRank: number | null
  listPrice: number | null
} | null>(null)

const refreshMutation = useRefreshProductFromCatalogMutation()

async function onRefresh() {
  if (!(await confirmModal.ask(`${props.asin} の商品情報をAmazonカタログの最新情報で上書きします。よろしいですか？`)))
    return

  refreshMutation.mutate(
    { asin: props.asin, req: { regionCode: props.regionCode, operator: auth.displayName } },
    {
      onSuccess: (res) => {
        // 取得結果を編集フォームへ反映する(VB版 SetUi 相当。DB保存はしない)。
        // ここでform.title等を書き換えると、下のwatch(...,scheduleCheck)が自動発火し、
        // VB版 ButtonRefresh_Click末尾の ButtonRefreshNgWords_Click 相当のNGワード
        // 再チェックも合わせて行われる(明示的な追加呼び出しは不要)。
        form.title = res.title ?? form.title
        form.brand = res.brand ?? form.brand
        form.editorialReview = res.editorialReview ?? form.editorialReview
        form.features = res.features ?? form.features
        form.color = res.color ?? form.color
        form.size = res.size ?? form.size
        form.adultFlg = res.adultFlg

        fetchedRawFields.value = {
          attributes: res.attributes,
          keyword: res.keyword,
          identifiers: res.identifiers,
          dimensions: res.dimensions,
          releaseDate: res.releaseDate,
          salesRank: res.salesRank,
          listPrice: res.listPrice,
        }

        resultModal.show(
          `Amazonカタログから情報を取得しました。内容を確認し、問題なければ「保存」を押してください。`,
        )
      },
      onError: (err) => resultModal.show((err as Error).message, 'ng'),
    },
  )
}

async function onSave() {
  if (!product.value) return
  // 商品編集画面(ProductEditView)と同じく、カテゴリ未選択のまま保存できないようにする。
  // プルダウン化したことで空のまま送信されるリスクが上がったため念のため確認する。
  if (!form.categoryCode) {
    resultModal.show('カテゴリを選択してください。', 'ng')
    return
  }
  if (!(await confirmModal.ask(`${props.asin} の商品情報を上書きします。よろしいですか？`))) return

  // 【重要】DBの既存データはVB.NET版(WPFのTextBox)由来でCRLF(\r\n)が
  // 標準になっている。エディタ内部(CodeMirror)はLF(\n)統一で扱っているため、
  // このまま保存するとDB上の改行コードがCRLFからLFへ変わってしまい、
  // 「同じテーブルの中でCRLFのレコードとLFのレコードが混在する」ことになる。
  // Amazonへの出品情報アップロードや他ツールが改行コードに依存していないかを
  // 完全には確認しきれないため、安全側に倒し、保存の瞬間だけ元の形式(CRLF)に
  // 戻す。表示・NGワード判定は引き続きLFで統一したまま行えるので、
  // 「保存するデータの形式を変えない」ことと「エディタ内の位置ズレを防ぐ」
  // ことを両立できる。
  const toCrLf = (t: string) => t.replace(/\n/g, '\r\n')

  saveMutation.mutate(
    {
      asin: props.asin,
      regionCode: props.regionCode,
      // 編集画面でしか出さない項目は、取得済みの値をそのまま送って変更しない
      categoryCode: form.categoryCode || null,
      binding: product.value.Binding,
      brand: form.brand || null,
      ean: form.ean || null,
      model: form.model || null,
      title: form.title || null,
      height: product.value.Height,
      length: product.value.Length,
      width: product.value.Width,
      packageWeight: product.value.PackageWeight,
      itemWeight: product.value.ItemWeight,
      releaseDate: fetchedRawFields.value?.releaseDate ?? product.value.ReleaseDate,
      size: form.size || null,
      color: form.color || null,
      listPrice: fetchedRawFields.value?.listPrice ?? product.value.ListPrice,
      salesRank: fetchedRawFields.value?.salesRank ?? product.value.SalesRank,
      editorialReview: form.editorialReview ? toCrLf(form.editorialReview) : null,
      editorialReviewLinkFlg: product.value.EditorialReviewLinkFlg,
      features: form.features ? toCrLf(form.features) : null,
      adultFlg: form.adultFlg,
      rating: product.value.Rating,
      attributes: fetchedRawFields.value?.attributes ?? product.value.Attributes,
      keyword: fetchedRawFields.value?.keyword ?? product.value.Keyword,
      identifiers: fetchedRawFields.value?.identifiers ?? product.value.Identifiers,
      dimensions: fetchedRawFields.value?.dimensions ?? product.value.Dimensions,
    },
    {
      onSuccess: () => {
        // 保存できたことは「モーダルが閉じる」こと自体で分かるため、
        // 別途「保存しました」の通知モーダルは出さない
        // (連続編集のテンポを損なうため。失敗時のみ気づいてほしいのでエラーは残す)。
        emit('saved')
        emit('close')
      },
      onError: (err) => resultModal.show(`保存に失敗しました: ${(err as Error).message}`, 'ng'),
    },
  )
}
</script>

<template>
  <!-- 入力中の誤操作で欄外クリックによって閉じてしまうと編集内容を失うため、
       ×ボタン・キャンセル・保存の明示的な操作でのみ閉じるようにする -->
  <div class="bim-overlay">
    <div class="bim-modal edit-modal" @click.stop>
      <div class="bim-head">
        <div class="bim-head-left">
          <div class="bim-title">商品情報を編集</div>
          <!-- VB版 ProductOverwriterDetailWindow ButtonRefresh_Click 相当。
               DB確定は保存ボタンのみ(親子関係のみ取得時点でDBへ反映される。詳細はonRefresh参照) -->
          <button
            class="btn btn-ghost btn-sm"
            type="button"
            title="Amazonカタログの最新情報を取得し、画面上の入力内容を上書きします(保存ボタンを押すまでDBには反映されません)"
            :disabled="refreshMutation.isPending.value"
            @click="onRefresh"
          >
            <svg class="icon icon-xs" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            {{ refreshMutation.isPending.value ? '取得中…' : '最新情報を取得' }}
          </button>
        </div>
        <button class="bim-close" @click="emit('close')">
          <svg class="icon" viewBox="0 0 24 24" style="width:18px;height:18px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div v-if="isLoading" class="empty-state" style="padding:24px 0;"><span class="spinner"></span> 読み込み中…</div>

      <div v-else class="edit-modal-body">
        <!-- プレビューが無い項目(1行の属性値)を先にまとめる。
             アダルト商品チェックを追加した分、4列×2行に収めて
             表示範囲(高さ)が広がらないようにしている。 -->
        <div class="edit-grid edit-grid--4col">
          <div class="edit-field">
            <label>カテゴリ</label>
            <!-- 商品編集画面(ProductEditView)と同じくプルダウン選択にする。
                 手打ちのブラウズノードIDだと誤入力の恐れがあるため。 -->
            <select v-model="form.categoryCode" class="edit-input">
              <option value="" disabled>選択してください</option>
              <option v-for="c in categories" :key="c.CategoryCode" :value="c.CategoryCode">
                {{ c.CategoryName }}
              </option>
            </select>
          </div>
          <div class="edit-field">
            <label>ブランド</label>
            <input v-model="form.brand" type="text" class="edit-input" />
          </div>
          <div class="edit-field">
            <label>モデル</label>
            <input v-model="form.model" type="text" class="edit-input" />
          </div>
          <div class="edit-field">
            <label>カラー</label>
            <input v-model="form.color" type="text" class="edit-input" />
          </div>
          <div class="edit-field">
            <label>サイズ</label>
            <input v-model="form.size" type="text" class="edit-input" />
          </div>
          <div class="edit-field">
            <label>EAN</label>
            <input v-model="form.ean" type="text" class="edit-input" />
          </div>
          <div class="edit-field edit-field--checkbox">
            <label class="edit-checkbox-label">
              <input v-model="form.adultFlg" type="checkbox" />
              アダルト商品
            </label>
          </div>
        </div>

        <!-- 商品名: 特徴・商品説明と同じくcontenteditableで直接マーキングする -->
        <div class="edit-field">
          <label>商品名</label>
          <NgEditableArea
            v-model="form.title"
            :ng-words="liveNgWords"
            field="title"
            :rows="3"
          />
        </div>

        <!-- 【試験実装】入力エリアそのものにNGワードの下線・ホバー説明を出す。
             左右分割はやめ、編集のみの単一エリアにする。
             従来のtextarea+プレビュー方式に戻したい場合は、このブロックを
             コメントアウトし、直後に残してある旧実装のブロックを有効化すればよい。 -->
        <div class="edit-field">
          <label>特徴</label>
          <!-- rowsを指定しない = .edit-textarea の既定height(340px)をそのまま使う -->
          <NgEditableArea
            v-model="form.features"
            :ng-words="liveNgWords"
            field="features"
          />
        </div>

        <div class="edit-field">
          <label>商品説明</label>
          <!-- rowsを指定しない = .edit-textarea の既定height(340px)をそのまま使う -->
          <NgEditableArea
            v-model="form.editorialReview"
            :ng-words="liveNgWords"
            field="description"
          />
        </div>

        <!-- ↓旧実装(左右分割+スクロール同期)。試験実装を戻す場合はこちらを使う。
        <div class="edit-field">
          <label>特徴</label>
          <div class="edit-split-head">
            <span>入力</span>
            <span></span>
            <span>プレビュー</span>
          </div>
          <div class="edit-split">
            <textarea
              :ref="(el) => { featuresSync.leftEl.value = el as HTMLTextAreaElement | null }"
              v-model="form.features"
              class="edit-textarea"
              rows="10"
              @scroll="featuresSync.onLeftScroll"
            ></textarea>
            <div class="edit-split-divider"></div>
            <div
              :ref="(el) => { featuresSync.rightEl.value = el as HTMLDivElement | null }"
              class="edit-preview"
              @scroll="featuresSync.onRightScroll"
            >
              <NgMarkedText :text="form.features" :ng-words="liveNgWords" preserve-whitespace field="features" />
            </div>
          </div>
        </div>

        <div class="edit-field">
          <label>商品説明</label>
          <div class="edit-split-head">
            <span>入力</span>
            <span></span>
            <span>プレビュー</span>
          </div>
          <div class="edit-split">
            <textarea
              :ref="(el) => { descriptionSync.leftEl.value = el as HTMLTextAreaElement | null }"
              v-model="form.editorialReview"
              class="edit-textarea"
              rows="12"
              @scroll="descriptionSync.onLeftScroll"
            ></textarea>
            <div class="edit-split-divider"></div>
            <div
              :ref="(el) => { descriptionSync.rightEl.value = el as HTMLDivElement | null }"
              class="edit-preview"
              @scroll="descriptionSync.onRightScroll"
            >
              <NgMarkedText :text="form.editorialReview" :ng-words="liveNgWords" preserve-whitespace field="description" />
            </div>
          </div>
        </div>
        -->
      </div>

      <div class="edit-modal-actions">
        <span v-if="checkMutation.isPending.value" class="edit-checking"><span class="spinner-sm"></span> NGワード確認中…</span>
        <button class="btn btn-ghost" @click="emit('close')">キャンセル</button>
        <button class="btn btn-primary" :disabled="saveMutation.isPending.value" @click="onSave">
          {{ saveMutation.isPending.value ? '保存中…' : '保存' }}
        </button>
      </div>
    </div>
  </div>
</template>
